import { Alert, Box, Typography } from "@mui/material";
import { ErrorIcon, WarningIcon } from "@teamworksdev/icons";
import { useOffsetPagination } from "apps/web/src/utils/pagination";
import { HistoricalTable } from "apps/web/src/views/Athlete/EnergyExpenditure/HistoricalOverview/HistoricalTable";
import { HistoricalTableSkeleton } from "apps/web/src/views/Athlete/EnergyExpenditure/HistoricalOverview/HistoricalTableSkeleton";
import { TodayOverviewCards } from "apps/web/src/views/Athlete/EnergyExpenditure/TodayOverview/TodayOverviewCards";
import { TodayOverviewSkeleton } from "apps/web/src/views/Athlete/EnergyExpenditure/TodayOverview/TodayOverviewSkeleton";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom-v5-compat";
import {
    useAthleteEnergyExpenditureHistoryQuery,
    useAthleteEnergyExpenditureInfoQuery,
    useAthleteEnergyExpenditureTodayOverviewQuery,
} from "../../../../types";

export const AthleteEnergyExpenditurePage = () => {
  const { athleteId: maybeAthleteId, teamId: maybeTeamId } = useParams();
  const athleteId = maybeAthleteId || "";
  const teamId = maybeTeamId || "";
  const [isRefetching, setIsRefetching] = useState(false);

  // Get user timezone (you may want to get this from user context)
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Pagination - 7 days per page
  const paginationHooks = useOffsetPagination(7);
  const { limit, offset, onChangePage } = paginationHooks;

  // Calculate date range for historical data (last 90 days)
  const { fromDate, toDate } = useMemo(() => {
    const today = new Date();
    const to = today.toISOString().split("T")[0]; // Today in YYYY-MM-DD format
    const from = new Date(today);
    from.setDate(from.getDate() - 90); // 90 days ago
    return { fromDate: from.toISOString().split("T")[0], toDate: to };
  }, []);

  // Fetch athlete info to check if they're linked to AMS
  const {
    data: athleteInfoData,
    loading: athleteInfoLoading,
    error: athleteInfoError,
  } = useAthleteEnergyExpenditureInfoQuery({
    variables: { athleteId },
    skip: !athleteId,
  });

  const athlete = athleteInfoData?.athlete;
  // axlePersonDocumentId comes from org_membership, so this checks both:
  // 1. Athlete has no org membership (returns null)
  // 2. Athlete has org membership but no axlePersonDocumentId (returns null)
  const isAthleteLinkedToAms = athlete?.axlePersonDocumentId != null;

  // Fetch today's overview (skip if athlete not linked to AMS)
  const {
    data: todayData,
    loading: todayLoading,
    error: todayError,
    refetch: refetchToday,
  } = useAthleteEnergyExpenditureTodayOverviewQuery({
    variables: { athleteId, timezone },
    skip: !athleteId || !isAthleteLinkedToAms,
    notifyOnNetworkStatusChange: true,
  });

  // Fetch historical data (skip if athlete not linked to AMS)
  const {
    data: historyData,
    loading: historyLoading,
    error: historyError,
    refetch: refetchHistory,
  } = useAthleteEnergyExpenditureHistoryQuery({
    variables: {
      athleteId,
      timezone,
      input: { limit, offset },
      from: fromDate,
      to: toDate,
    },
    skip: !athleteId || !isAthleteLinkedToAms,
    notifyOnNetworkStatusChange: true,
  });

  const handlePageChange = (newOffset: number, _newLimit: number) => {
    const newPage = newOffset / limit;
    onChangePage(newPage);
  };

  const handleRefresh = async () => {
    setIsRefetching(true);
    try {
      await Promise.all([refetchToday(), refetchHistory()]);
    } finally {
      setIsRefetching(false);
    }
  };

  const todayOverview = todayData?.athleteEnergyExpenditureTodayOverview;
  const history = historyData?.athleteEnergyExpenditureHistory;

  // Check if today's overview has no AMS data (all energy values are null)
  const todayHasNoAmsData = !!(todayOverview &&
    todayOverview.tdee === null &&
    todayOverview.activeEnergy === null &&
    todayOverview.restingEnergy === null);

  const showTodaySkeleton = athleteInfoLoading || (isAthleteLinkedToAms && (todayLoading || isRefetching));
  const showHistorySkeleton = athleteInfoLoading || (isAthleteLinkedToAms && (historyLoading || isRefetching));

  // Show error if athlete info failed to load
  if (athleteInfoError) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, px: 1 }}>
        <Box>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h3">Energy Expenditure</Typography>
          </Box>
          <Typography sx={{ fontFamily: "Inter", color: "#667085" }}>Error loading athlete information. Please try again.</Typography>
        </Box>
      </Box>
    );
  }

  // Check error types
  const getErrorType = (error: any): '4xx' | 'generic' | null => {
    if (!error || error.message.includes("not linked")) return null;

    // Check for 4xx status codes in GraphQL errors
    if (error.graphQLErrors?.length > 0) {
      const gqlError = error.graphQLErrors[0];

      const statusCode = gqlError.extensions?.response?.status ||
                        gqlError.extensions?.response?.statusCode ||
                        gqlError.extensions?.statusCode ||
                        gqlError.extensions?.code;

      if (statusCode && statusCode >= 400 && statusCode < 500) {
        return '4xx';
      }
    }

    // Check for 4xx status codes in network errors
    if (error.networkError?.statusCode >= 400 && error.networkError?.statusCode < 500) {
      return '4xx';
    }

    // Check error message for common 4xx indicators
    const message = error.message?.toLowerCase() || '';
    if (message.includes('unauthorized') ||
        message.includes('forbidden') ||
        message.includes('not found') ||
        message.includes('401') ||
        message.includes('403') ||
        message.includes('404')) {
      return '4xx';
    }

    return 'generic';
  };

  const todayErrorType = getErrorType(todayError);
  const historyErrorType = getErrorType(historyError);
  const has4xxError = todayErrorType === '4xx' || historyErrorType === '4xx';
  const hasGenericError = todayErrorType === 'generic' || historyErrorType === 'generic';
  const showEmptyState = !isAthleteLinkedToAms || has4xxError || hasGenericError;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, px: 1 }}>
      {/* Banner for athletes not linked to AMS or generic errors */}
      {showEmptyState && (
        <Alert
          severity={has4xxError || hasGenericError ? "error" : "warning"}
          icon={
            has4xxError || hasGenericError ? (
              <ErrorIcon style={{ width: 20, height: 20, color: "#d32f2f" }} />
            ) : (
              <WarningIcon style={{ width: 20, height: 20, color: "#ef6820" }} />
            )
          }
          sx={{
            mb: 1,
            backgroundColor: has4xxError || hasGenericError ? "#ffebee" : "#fef4e6",
            borderRadius: "6px",
            padding: "16px",
            border: "none",
            "& .MuiAlert-icon": {
              color: has4xxError || hasGenericError ? "#d32f2f" : "#ef6820",
            },
            "& .MuiAlert-message": {
              color: "rgba(0, 0, 0, 0.87)",
              fontFamily: "Inter",
            },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {has4xxError ? (
              <>
                <Typography sx={{ fontWeight: 600, fontSize: "14px", lineHeight: "18px" }}>Access Denied</Typography>
                <Typography sx={{ fontWeight: 400, fontSize: "14px", lineHeight: "18px" }}>
                  You do not have access to this athlete's data.
                </Typography>
              </>
            ) : hasGenericError ? (
              <>
                <Typography sx={{ fontWeight: 600, fontSize: "14px", lineHeight: "18px" }}>Unable to Load Data</Typography>
                <Typography sx={{ fontWeight: 400, fontSize: "14px", lineHeight: "18px" }}>
                  We are unable to load this data right now, retry or reach out to support for assistance.
                </Typography>
              </>
            ) : (
              <>
                <Typography sx={{ fontWeight: 600, fontSize: "14px", lineHeight: "18px" }}>Setup Required</Typography>
                <Typography sx={{ fontWeight: 400, fontSize: "14px", lineHeight: "18px" }}>
                  This athlete needs further setup in order to display data below.
                </Typography>
              </>
            )}
          </Box>
        </Alert>
      )}
      {/* Today's Overview Section */}
      {showTodaySkeleton ? (
        <TodayOverviewSkeleton />
      ) : (
        <TodayOverviewCards
          data={todayOverview}
          athleteId={athleteId}
          teamId={teamId}
          onRefresh={handleRefresh}
          showEmptyState={showEmptyState || !todayOverview || todayHasNoAmsData}
        />
      )}

      {/* Historical Overview Section */}
      <Box>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h3">Historical Overview</Typography>
        </Box>
        {showHistorySkeleton ? (
          <HistoricalTableSkeleton />
        ) : (
          <HistoricalTable
            data={history}
            onPageChange={handlePageChange}
            offset={offset}
            limit={limit}
            athleteId={athleteId}
            teamId={teamId}
            showEmptyState={showEmptyState || !history || history.edges.length === 0}
          />
        )}
      </Box>
    </Box>
  );
};
